import { create, tsx } from '@dojo/framework/core/vdom';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { FAQ } from '../../widgets/FAQ';
import { Hero } from '../../widgets/Hero';
import { Summary } from '../../widgets/Summary';
import SummaryList from '../../widgets/SummaryList';

import * as css from './PerformanceOptimization.m.css';

import Head from '../../widgets/Head';
import { IndustryExpertise } from '../../widgets/IndustryExpertise';
import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import Image from '../../widgets/Image';
import SitePenLogo from '../../widgets/SitepenLogo';

const analysisImg = require('../../assets/images/illustration/analysis.svg');
const investmentROIImg = require('../../assets/images/icon-illustration/investment-roi.svg');
const enterpriseApplicationIntegrationImg = require('../../assets/images/icon-illustration/enterprise-application-integration.svg');
const teamEfficiencyImg = require('../../assets/images/illustration/team-efficiency.svg');
const highPerformanceImg = require('../../assets/images/illustration/high-performance.svg');
const reduceCostsImg = require('../../assets/images/icon-illustration/reduce-costs.svg');

const factory = create();

export const PerformanceOptimization = factory(function PerformanceOptimization() {
	return (
		<div classes={css.root}>
			<Head
				title="Performance Optimization | SitePen"
				description="Our company specializes in software performance optimization to ensure your application runs as efficiently and effectively as possible."
			/>

			<Hero background="level3Blue">
				{{
					xlTitle: (
						<div>
							Performance
							<br />
							Optimization
						</div>
					),
					blurb:
						"The best software applications go beyond allowing the users to do their work; they deliver fast, responsive experiences that don't waste the user's time."
				}}
			</Hero>

			<Section type="leading">
				<ContentWrapper>
					<SummaryList
						trailingBias="medium"
						listGuttering="none"
						style="alternating-reverse"
						itemPaddingBottom="md"
					>
						{{
							items: [
								{
									title: 'Optimize Your Software Performance',
									blurb: (
										<virtual>
											<p>
												In the world of software, the term "performance
												optimization" is often associated with maximizing an
												application's speed. While this is undoubtedly true,
												proper optimization requires a balance of multiple,
												often conflicting, parameters.
											</p>
											<p>
												At SitePen, we believe that there is a point at
												which many software systems, especially user-facing
												ones, are fast enough. In most cases, an application
												is fast enough if it is waiting on the user versus
												the user waiting on it. Anything more is ultimately
												a waste of resources and not true optimization.
												Additionally, focusing purely on the application's
												speed can compromise other performance parameters,
												such as memory usage and power consumption.
											</p>
											<p>
												Proper system optimization requires a strong
												foundational understanding of what the system will
												be used for. A data analysis application used by a
												small team of specialists needs to be optimized in a
												very different way than an eCommerce site with
												thousands or millions of potential users.
											</p>
										</virtual>
									),
									image: <Image src={analysisImg} size="lg" />
								},
								{
									blurb: (
										<virtual>
											<p>
												The team at SitePen has experience with applications
												that have a wide variety of performance
												requirements. As we work with you to document the
												system's needs, we will identify critical
												performance constraints such as the number of
												potential users, the hosting infrastructure's
												capabilities, and the ideal level of responsiveness
												that the system should give.
											</p>
											<p>
												Our engineering team combines these performance
												constraints with all of the other requirements to
												synthesize a holistic system architecture that
												allows the software to be written clearly and
												efficiently. Internal quality controls are used
												throughout the design process to ensure that the
												system will meet your expectations without being
												burdened by over-engineered elements that can
												unnecessarily slow down development velocity.
											</p>
										</virtual>
									),
									image: <SitePenLogo />
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>

			<Section shaded>
				<ContentWrapper>
					<SummaryList>
						{{
							title: 'Software Optimization Benefits',
							blurb: (
								<virtual>
									Optimized software systems offer a pleasant user experience
									while minimizing operating costs and maintenance complexity.
								</virtual>
							),
							items: [
								{
									title: 'Improved Application Performance',
									blurb: (
										<virtual>
											<p>
												Improving application performance is the most common
												area of system optimization work. Users waiting for
												a system to process a request cannot add value to
												the process, which is both inefficient and
												frustrating. It can be tempting to address these
												issues by increasing the hosting infrastructure's
												power, but this often yields suboptimal results and
												can be very expensive.
											</p>
											<p>
												Our team approaches the challenge of improving
												performance before the first line of code is even
												written. An efficient system design will often lead
												to systems that never encounter performance issues
												at all. When a bottleneck does occur, our engineers
												make extensive use of performance analysis tools to
												pinpoint the root cause of the degradation and
												address the underlying cause.
											</p>
										</virtual>
									),
									image: (
										<Image
											src={enterpriseApplicationIntegrationImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'Reduced Operating Costs',
									blurb: (
										<virtual>
											The most optimized system is not the fastest one. It is
											the one that is just fast enough under heavy loading,
											which minimizing the resources required to run it. Many
											software development companies focus only on the
											software that they are responsible for writing. At
											SitePen, we consider the hosting servers to be just as
											much a part of the system and, therefore, part of the
											scope for optimization. We work with our clients to
											properly provision their servers to strike the ideal
											balance between application responsiveness and operating
											costs.
										</virtual>
									),
									image: (
										<Image
											src={reduceCostsImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'Better Return on Investment (ROI)',
									blurb: (
										<virtual>
											<p>
												Performance optimization has two primary influences
												on a system's ROI. One the most noticeable effects
												is on long-term costs. An optimized system is a good
												steward of the resources that are made available to
												it. These systems meet their users' expectations for
												speed and responsiveness while minimizing the
												processing time, memory, and storage that they
												require. Less obvious but equally important is the
												short-term effect that performance optimization has
												on a project's ROI. Almost any system can be made to
												go faster or use fewer resources. However, as the
												system's efficiency increases, it becomes
												increasingly challenging to find non-trivial gains
												in these areas. Too much focus on long-term ROI can
												lead to an excessively over-engineered system whose
												performance does not justify development costs.
											</p>
											<p>
												At SitePen, we believe that an application's ideal
												performance balances the system's responsiveness and
												resource utilization with the time and effort
												required to achieve those targets.
											</p>
										</virtual>
									),
									image: (
										<Image
											src={investmentROIImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section>
				<IndustryExpertise />
			</Section>
			<Section shaded>
				<ContentWrapper size="small">
					<Summary stack>
						{{
							image: <Image src={teamEfficiencyImg} lazy size="lg" />,
							title: 'Choosing the Right Performance Optimization Team',
							blurb: (
								<virtual>
									<p>
										The right performance optimization team for any project must
										have two characteristics - it must be skilled enough to
										achieve the speed and responsiveness that users require and
										disciplined enough to avoid wasteful over-optimization.
									</p>
									<p>
										Today's software systems are so complicated that it can be
										difficult to meet users' expectations for responsiveness.
										The development team must start by identifying what the
										users' expectations and needs truly are. It is critical to
										quantify the speed required to allow users to efficiently
										move through each workflow and ensure those requirements are
										met via automated testing.
									</p>
									<p>
										One of the most challenging things that many software
										engineers face is defining "good enough." The best engineers
										are continually pushing themselves and their code to be more
										efficient. While this is an excellent trait, it can lead to
										over-engineered solutions whose development time does not
										justify the performance benefits. A disciplined optimization
										team is aware of these tendencies and has controls in place
										to prevent these over-optimizations.
									</p>
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>

			<Section>
				<ContentWrapper>
					<Summary>
						{{
							title: 'Benefits of Software Optimization Services',
							blurb:
								'Software optimization requires a specialized set of skills, and many companies can benefit from sourcing those skills externally. An external optimization team brings a wealth of experience to the optimization project that has been honed through multiple projects across various industries and project sizes. Additionally, an external team brings a fresh perspective to optimization projects that can significantly improve system responsiveness and operating costs.',
							image: <Image src={highPerformanceImg} lazy size="lg" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>

			<Section shaded>
				<ConnectStrip />
			</Section>
			<Section trailing>
				<FAQ title="Performance Optimization FAQs">
					{[
						{
							title: 'When Should Optimization Be Performed?',
							content: (
								<p>
									Ideally, performance optimization shouldn't be an event that
									occurs at a specific point in the development process. An
									application's performance targets should be identified early and
									influence the project's design, development, and verification
									phases. However, care must be taken to avoid placing too much
									focus on optimization early in the process to avoid
									over-engineering the solution.
								</p>
							)
						},
						{
							title: 'Why Should I Optimize My Software?',
							content: (
								<p>
									An unoptimized software system is susceptible to two major
									faults. First, the users of the system might find it sluggish
									and unresponsive. Not only does this affect the users'
									efficiency, but it can also encourage them to seek other
									solutions that are more responsive, even if they have other
									flaws. The unoptimized system might also consume excessive
									computing resources, leading to increased operational costs and
									decreased ROI. Addressing the system's performance throughout
									the development process is more likely to provide a responsive
									user experience without introducing excessive load on its
									hosting infrastructure.
								</p>
							)
						},
						{
							title: 'How Long Does Performance Optimization Take?',
							content: (
								<p>
									When incorporated into the entire development process,
									performance optimization has a minimal impact on the project's
									timeline and resource costs. There will be some impact during
									the testing and validation phases of development, but those
									effects are generally minor. If optimization is delayed until
									the end of the project, then a significant amount of time might
									be required since substantial rework of the application might be
									necessary to address any discovered defects.
								</p>
							)
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default PerformanceOptimization;
